import Num, { BigNumber } from '@flipster/utils/num';

import type { BracketTierWithMaintAmount } from './maint-amount';
import { toFiniteDecimal, toNonNegativeDecimal, toPositiveDecimal } from './number-guards';

interface WalkBracketsForLiqPriceArgs {
  marginAssigned: string;
  unrealizedCost: string;
  signedSize: string;
  tiers: BracketTierWithMaintAmount[];
  tickSize: string;
}

const findSeedTierIndex = (notional: BigNumber, tiers: BracketTierWithMaintAmount[]) => {
  const index = tiers.findIndex((tier) => Num(tier.notionalPositionSizeUpperLimit).gte(notional));
  return index === -1 ? tiers.length - 1 : index;
};

const isPositiveQuotient = (numerator: BigNumber, denominator: BigNumber) => {
  if (numerator.eq(0)) return false;
  return numerator.gt(0) === denominator.gt(0);
};

const hasValidTiers = (tiers: BracketTierWithMaintAmount[]) =>
  tiers.every((tier) => {
    const notionalUpperLimit = toPositiveDecimal(tier.notionalPositionSizeUpperLimit);
    const maintMarginRate = toNonNegativeDecimal(tier.maintMarginRate);
    const maintAmount = toNonNegativeDecimal(tier.maintAmount);

    return !!notionalUpperLimit && !!maintMarginRate && !!maintAmount;
  });

// Cross-multiply: (num / den) ? value
//   num / den > value   ⇔  sign(den) * (num - den*value) > 0
//   num / den < value   ⇔  sign(den) * (num - den*value) < 0
// Avoids the BigNumber default-precision divide for tier-boundary checks.
const compareQuotientToDecimal = (numerator: BigNumber, denominator: BigNumber, value: BigNumber) => {
  const lhs = numerator.minus(denominator.multipliedBy(value));
  const cmp = lhs.comparedTo(0);
  if (cmp === null) return 0;
  return denominator.gt(0) ? cmp : -cmp;
};

const roundQuotientToTickSize = (
  numerator: BigNumber,
  denominator: BigNumber,
  tickSize: BigNumber,
  isLong: boolean,
) => {
  const quotient = numerator.dividedBy(denominator);
  if (!quotient.gt(0)) return null;

  const ticks = quotient.dividedBy(tickSize);
  const roundedTicks = isLong ? ticks.integerValue(BigNumber.ROUND_CEIL) : ticks.integerValue(BigNumber.ROUND_FLOOR);
  const rounded = roundedTicks.multipliedBy(tickSize);

  return rounded.lte(0) ? null : rounded;
};

/**
 * Mirrors `prex-core::risk::liquidation::calculate_liq_price`'s tier walk.
 * Solves the post-fill liq price in closed form per tier and steps to a
 * neighbour tier when the candidate price exits the current tier's notional
 * window. Returns null when no positive solution exists in any tier.
 */
export function walkBracketsForLiqPrice(args: WalkBracketsForLiqPriceArgs): string | null {
  if (args.tiers.length === 0) return null;

  const marginAssigned = toFiniteDecimal(args.marginAssigned);
  const unrealizedCost = toFiniteDecimal(args.unrealizedCost);
  const signedSize = toFiniteDecimal(args.signedSize);
  const tickSize = toPositiveDecimal(args.tickSize);

  if (!marginAssigned || !unrealizedCost || !signedSize || !tickSize || !hasValidTiers(args.tiers)) return null;

  const positionSize = signedSize.abs();

  if (signedSize.eq(0)) return null;

  const isLong = signedSize.gt(0);
  let tierIndex = findSeedTierIndex(unrealizedCost.abs(), args.tiers);
  let eventPriceNumerator: BigNumber | null = null;
  let eventPriceDenominator: BigNumber | null = null;

  // Bounded by tier count; loop break either returns or steps a single tier.
  while (true) {
    const tier = args.tiers[tierIndex];
    const numerator = marginAssigned.minus(unrealizedCost).plus(tier.maintAmount);
    const denominator = Num(tier.maintMarginRate).multipliedBy(positionSize).minus(signedSize);

    if (denominator.eq(0)) return null;
    if (!isPositiveQuotient(numerator, denominator)) return null;

    if (isLong) {
      const tierLowerLimit = tierIndex === 0 ? Num('0') : Num(args.tiers[tierIndex - 1].notionalPositionSizeUpperLimit);
      const tierExitPrice = tierLowerLimit.dividedBy(positionSize);
      if (compareQuotientToDecimal(numerator, denominator, tierExitPrice) >= 0 || tierIndex === 0) {
        eventPriceNumerator = numerator;
        eventPriceDenominator = denominator;
        break;
      }
      tierIndex -= 1;
    } else {
      const tierExitPrice = Num(tier.notionalPositionSizeUpperLimit).dividedBy(positionSize);
      if (compareQuotientToDecimal(numerator, denominator, tierExitPrice) <= 0 || tierIndex === args.tiers.length - 1) {
        eventPriceNumerator = numerator;
        eventPriceDenominator = denominator;
        break;
      }
      tierIndex += 1;
    }
  }

  if (!eventPriceNumerator || !eventPriceDenominator) return null;

  const rounded = roundQuotientToTickSize(eventPriceNumerator, eventPriceDenominator, tickSize, isLong);
  return rounded?.toString() ?? null;
}
